import React, { useRef, useState } from 'react';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { EDM_IMPORT_MAX_BYTES, edmImportExt } from '../../../../hooks/useEdmImport';
import type { EdmImportExt } from '../../../../hooks/useEdmImport';

const ACCEPT =
  '.csv,.xlsx,.pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf';

/**
 * Drop/click target for one contact file. Markup follows the Setup Wizard's
 * ContactsDropzone (not exported, and CSV-only). Checks extension and size
 * before any network call; the storage rule and callable are the authority.
 */
const ImportDropzone: React.FC<{
  disabled?: boolean;
  progress: number | null;
  fileName: string | null;
  onFile: (file: File, ext: EdmImportExt) => void;
}> = ({ disabled, progress, fileName, onFile }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handle = (file: File) => {
    const ext = edmImportExt(file.name);
    if (!ext) {
      setError('CSV, Excel (.xlsx) or PDF files only. Old .xls files: open in Excel and Save As .xlsx.');
      return;
    }
    if (file.size >= EDM_IMPORT_MAX_BYTES) {
      setError('File too large: 10 MB max.');
      return;
    }
    setError(null);
    onFile(file, ext);
  };

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const file = e.dataTransfer.files?.[0];
          if (file && !disabled) handle(file);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed border-gray-700 p-6 text-center transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-600 hover:bg-gray-800/40'
        }`}
      >
        {fileName ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-200">
            <FileSpreadsheet className="h-5 w-5 text-blue-400" /> {fileName}
          </div>
        ) : (
          <>
            <Upload className="mx-auto mb-2 h-5 w-5 text-gray-500" />
            <p className="text-sm text-gray-400">Click or drop a CSV, Excel (.xlsx) or PDF file, up to 10 MB</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          data-testid="edm-import-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handle(file);
            e.target.value = '';
          }}
        />
      </div>
      {progress !== null && (
        <div>
          <div className="mb-1 text-xs text-gray-300">Uploading… {progress}%</div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-900">
            <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

export default ImportDropzone;
